import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Tune as TuneIcon,
  Straighten as UnitsIcon,
  Group as UsersIcon,
  Save as SaveIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import { companiesAPI as companyAPI, authAPI } from '../services';
import { useAuth } from '../context/AuthContext';
import { hasPermission, getUserCompanyId } from '../utils/userPermissions';
import { TIMEZONE_OPTIONS } from '../utils/timezoneOptions';
import UsersPage from './Users';
import config from '../config';

interface Company {
  companyid: string;
  companyname: string;
  companytype: string;
  logo?: string;
  timezone?: string;
  pourcode?: string;
  reportprefix?: string;
  reportno?: string;
  dateformat?: string;
  tempformat?: string;
  evaformat?: string;
  strengthformat?: string;
  densityformat?: string;
  loggercalibrationfrequency?: number;
  address?: string;
  phone?: string;
  hasmass?: boolean;
  hasmaturity?: boolean;
  hasmatchcuring?: boolean;
  hasweather?: boolean;
  haseva?: boolean;
  hasexternalth?: boolean;
  contactname?: string;
  contactemail?: string;
  windspeedformat?: string;
  isactive?: boolean;
}

type SectionKey = 'client-details' | 'general' | 'units' | 'users';

// Fields each tab saves. Must stay inside the backend's
// CLIENT_EDITABLE_FIELDS whitelist (routers/companies.py).
const SECTION_FIELDS: Record<Exclude<SectionKey, 'users'>, (keyof Company)[]> = {
  'client-details': ['address', 'phone', 'timezone', 'contactname', 'contactemail'],
  general: ['dateformat', 'pourcode', 'reportprefix', 'reportno', 'loggercalibrationfrequency'],
  units: ['tempformat', 'strengthformat', 'evaformat', 'densityformat', 'windspeedformat'],
};

const MAX_LOGO_MB = 5;

// Effective licensed pour functions, mirroring backend monitoring_access:
// all flags NULL = never configured = full access; otherwise NULL is off
// except External T&H (flag postdates the others, NULL = granted), and
// prerequisites are pulled in.
const effectiveLicensing = (company: Company) => {
  const raw = [
    company.hasmass,
    company.hasmaturity,
    company.hasmatchcuring,
    company.hasweather,
    company.haseva,
    company.hasexternalth,
  ];
  const unconfigured = raw.every((v) => v === null || v === undefined);
  const mass = !!(company.hasmass || company.hasmaturity || company.hasmatchcuring || company.haseva);
  return {
    hasmass: unconfigured || mass,
    hasmaturity: unconfigured || !!(company.hasmaturity || company.hasmatchcuring),
    hasmatchcuring: unconfigured || !!company.hasmatchcuring,
    hasweather: unconfigured || !!company.hasweather,
    haseva: unconfigured || !!company.haseva,
    hasexternalth: unconfigured || company.hasexternalth !== false,
  };
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { section } = useParams<{ section: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();

  const companyId = String(getUserCompanyId(user) || '');
  const canEdit = hasPermission(user, 'settings.edit_client_details');
  // The Users tab is the client-scoped user list (matrix row "View user
  // list"), so Project Admin sees it too; row actions inside the embedded
  // page are gated by their own permissions.
  const showUsersTab = hasPermission(user, 'users.view_list');

  const activeSection: SectionKey =
    section === 'general' || section === 'units' || (section === 'users' && showUsersTab)
      ? (section as SectionKey)
      : 'client-details';

  const [company, setCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<Partial<Company>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchCompany = useCallback(async () => {
    if (!companyId) {
      setError(t('settings.noCompany', 'No company is associated with this account.'));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await companyAPI.getCompanyById(companyId);
      setCompany(response as unknown as Company);
      setFormData(response as unknown as Partial<Company>);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('settings.fetchError', 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleChange = (field: keyof Company) => (event: any) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (activeSection === 'users' || !companyId) return;
    const payload: Record<string, unknown> = {};
    for (const field of SECTION_FIELDS[activeSection]) {
      const value = formData[field];
      if (value !== undefined && value !== null && value !== '') {
        payload[field] = value;
      }
    }
    try {
      setSaving(true);
      setError(null);
      await companyAPI.updateCompany(companyId, payload);
      setSuccess(true);
      await fetchCompany();
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('settings.saveError', 'Failed to save settings'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !companyId) return;
    if (!file.type.startsWith('image/')) {
      setUploadError(t('generalSettings.logoTypeError', 'Please choose an image file (PNG, JPG, GIF or WebP).'));
      return;
    }
    if (file.size > MAX_LOGO_MB * 1024 * 1024) {
      setUploadError(t('generalSettings.logoSizeError', { defaultValue: 'File too large. Maximum size is {{mb}}MB.', mb: MAX_LOGO_MB }));
      return;
    }
    try {
      setUploading(true);
      setUploadError(null);
      await companyAPI.uploadLogo(companyId, file);
      // The header bar shows this logo from the cached user; refresh it and
      // reload so the change is visible everywhere immediately.
      try {
        const me = await authAPI.getCurrentUser();
        localStorage.setItem('user', JSON.stringify(me));
      } catch {
        /* header updates on next login if the refresh fails */
      }
      window.location.reload();
    } catch (err: any) {
      setUploadError(err?.response?.data?.detail || t('generalSettings.logoUploadFailed', 'Failed to upload logo'));
    } finally {
      setUploading(false);
    }
  };

  const sections: { key: SectionKey; label: string; icon: React.ReactElement }[] = [
    { key: 'client-details', label: t('settings.clientDetails', 'Client Details'), icon: <BusinessIcon fontSize="small" /> },
    { key: 'general', label: t('settings.generalSettings', 'General Settings'), icon: <TuneIcon fontSize="small" /> },
    { key: 'units', label: t('settings.unitsOfMeasure', 'Units of Measure'), icon: <UnitsIcon fontSize="small" /> },
    ...(showUsersTab
      ? [{ key: 'users' as SectionKey, label: t('settings.users', 'Users'), icon: <UsersIcon fontSize="small" /> }]
      : []),
  ];

  // ── Shared render fragments ────────────────────────────────────────────

  const fieldRow = (label: string, control: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        gap: { xs: 0.5, sm: 2 },
        mb: 2.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, width: { sm: 260 }, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, maxWidth: 420 }}>{control}</Box>
    </Box>
  );

  const lockedRow = (label: string, value: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        gap: { xs: 0.5, sm: 2 },
        mb: 2.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, width: { sm: 260 }, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{value}</Box>
    </Box>
  );

  const saveBar = (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ px: 5 }}
        >
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </Button>
      </Box>
    </Box>
  );

  // ── Sections ───────────────────────────────────────────────────────────

  const renderClientDetails = () => {
    if (!company) return null;
    const licensing = effectiveLicensing(company);
    const features: { label: string; active: boolean }[] = [
      { label: t('settings.featTemperatureOnly', 'Temperature Only'), active: licensing.hasmass },
      { label: t('settings.featConcreteMaturity', 'Concrete Maturity'), active: licensing.hasmaturity },
      { label: t('settings.featTmc', 'Temperature Match Curing'), active: licensing.hasmatchcuring },
      { label: t('settings.featWeather', 'Weather Stations'), active: licensing.hasweather },
      { label: t('settings.featEva', 'Concrete Evaporation Rates'), active: licensing.haseva },
      { label: t('settings.featEnviro', 'Environmental Conditions'), active: licensing.hasexternalth },
    ];
    const logoUrl = company.logo
      ? `${config.apiBaseUrl}/files/company-logos/${company.companyid}/${company.logo}`
      : undefined;

    return (
      <>
        {lockedRow(
          t('settings.clientCompanyName', 'Client / Company Name'),
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{company.companyname}</Typography>,
        )}

        {fieldRow(
          t('settings.clientCompanyLogo', 'Client / Company Logo'),
          <Box>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setUploadError(null)}>
                {uploadError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt={company.companyname}
                  sx={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('settings.noLogo', 'No logo uploaded')}
                </Typography>
              )}
              {canEdit && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={16} /> : <CameraIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading
                      ? t('settings.uploading', 'Uploading...')
                      : t('settings.changeLogo', 'Change Logo')}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleLogoChange}
                  />
                </>
              )}
            </Box>
          </Box>,
        )}

        {fieldRow(
          t('settings.clientCompanyAddress', 'Client / Company Address'),
          <TextField
            size="small"
            fullWidth
            value={formData.address || ''}
            onChange={handleChange('address')}
            disabled={!canEdit}
          />,
        )}

        {fieldRow(
          t('settings.clientCompanyContactNumber', 'Client / Company Contact Number'),
          <TextField
            size="small"
            fullWidth
            value={formData.phone || ''}
            onChange={handleChange('phone')}
            disabled={!canEdit}
          />,
        )}

        {fieldRow(
          t('settings.timeZone', 'Time Zone'),
          <FormControl size="small" fullWidth disabled={!canEdit}>
            <Select value={formData.timezone || ''} onChange={handleChange('timezone')} displayEmpty>
              {TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>,
        )}

        {fieldRow(
          t('settings.contactName', 'Contact Name'),
          <TextField
            size="small"
            fullWidth
            value={formData.contactname || ''}
            onChange={handleChange('contactname')}
            disabled={!canEdit}
          />,
        )}

        {fieldRow(
          t('settings.contactEmail', 'Contact e-mail address'),
          <TextField
            size="small"
            fullWidth
            type="email"
            value={formData.contactemail || ''}
            onChange={handleChange('contactemail')}
            disabled={!canEdit}
          />,
        )}

        {lockedRow(
          t('settings.accountStatus', 'Account Status'),
          <Chip
            size="small"
            label={company.isactive ? t('common.active', 'Active') : t('common.inactive', 'Not Active')}
            color={company.isactive ? 'success' : 'default'}
          />,
        )}

        {lockedRow(
          t('settings.systemFeatures', 'System Features'),
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {features.map((f) => (
              <Box key={f.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: f.active ? 600 : 400, minWidth: 220 }}>
                  {f.label}
                </Typography>
                <Chip
                  size="small"
                  label={f.active ? t('common.active', 'Active') : t('settings.notActive', 'Not Active')}
                  color={f.active ? 'success' : 'default'}
                  variant={f.active ? 'filled' : 'outlined'}
                />
              </Box>
            ))}
          </Box>,
        )}

        {canEdit && saveBar}
      </>
    );
  };

  const renderGeneral = () => (
    <>
      {fieldRow(
        t('settings.defaultFormat', 'Default Format'),
        <FormControl size="small" fullWidth disabled={!canEdit}>
          <Select value={formData.dateformat || ''} onChange={handleChange('dateformat')} displayEmpty>
            <MenuItem value="01">{t('companyEdit.dateFormatAU', 'DD/MM/YYYY (AU)')}</MenuItem>
            <MenuItem value="02">{t('companyEdit.dateFormatUS', 'MM/DD/YYYY (US)')}</MenuItem>
          </Select>
        </FormControl>,
      )}

      {fieldRow(
        t('settings.pourReferencePrefix', 'Pour Reference Prefix'),
        <TextField
          size="small"
          fullWidth
          value={formData.pourcode || ''}
          onChange={handleChange('pourcode')}
          disabled={!canEdit}
        />,
      )}

      {fieldRow(
        t('settings.reportPrefix', 'Report Prefix'),
        <TextField
          size="small"
          fullWidth
          value={formData.reportprefix || ''}
          onChange={handleChange('reportprefix')}
          disabled={!canEdit}
          inputProps={{ maxLength: 6 }}
        />,
      )}

      {fieldRow(
        t('settings.reportNumber', 'Report Number'),
        <TextField
          size="small"
          fullWidth
          value={formData.reportno || ''}
          onChange={handleChange('reportno')}
          disabled={!canEdit}
        />,
      )}

      {fieldRow(
        t('settings.calibNotificationFrequency', 'Hardware Calibration Notification Frequency'),
        <Box>
          <FormControl size="small" fullWidth disabled={!canEdit}>
            <Select
              value={formData.loggercalibrationfrequency ?? ''}
              onChange={handleChange('loggercalibrationfrequency')}
              displayEmpty
            >
              {[3, 6, 12, 24]
                .concat(
                  typeof formData.loggercalibrationfrequency === 'number' &&
                    ![3, 6, 12, 24].includes(formData.loggercalibrationfrequency)
                    ? [formData.loggercalibrationfrequency]
                    : [],
                )
                .sort((a, b) => a - b)
                .map((months) => (
                  <MenuItem key={months} value={months}>
                    {t('settings.monthsValue', { defaultValue: '{{count}} months', count: months })}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {t('settings.calibNotificationHint', 'An email is sent when logger calibration is due')}
          </Typography>
        </Box>,
      )}

      {lockedRow(
        t('settings.hardwareAccuracySpec', 'Hardware Accuracy Specification'),
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('settings.notConfigured', 'Not configured')}
        </Typography>,
      )}

      {canEdit && saveBar}
    </>
  );

  const renderUnits = () => (
    <>
      {fieldRow(
        t('settings.temperatureUnits', 'Temperature Units'),
        <FormControl size="small" fullWidth disabled={!canEdit}>
          <Select value={formData.tempformat || ''} onChange={handleChange('tempformat')} displayEmpty>
            <MenuItem value="01">{t('companyEdit.celsius', 'Celsius (°C)')}</MenuItem>
            <MenuItem value="02">{t('companyEdit.fahrenheit', 'Fahrenheit (°F)')}</MenuItem>
          </Select>
        </FormControl>,
      )}

      {fieldRow(
        t('settings.strengthUnits', 'Strength Units'),
        <FormControl size="small" fullWidth disabled={!canEdit}>
          <Select value={formData.strengthformat || ''} onChange={handleChange('strengthformat')} displayEmpty>
            <MenuItem value="01">{t('companyEdit.strengthMpa', 'MPa')}</MenuItem>
            <MenuItem value="02">{t('companyEdit.strengthPsi', 'psi')}</MenuItem>
            <MenuItem value="03">{t('companyEdit.strengthNmm', 'N/mm²')}</MenuItem>
          </Select>
        </FormControl>,
      )}

      {fieldRow(
        t('settings.evaporationUnits', 'Evaporation Units'),
        <FormControl size="small" fullWidth disabled={!canEdit}>
          <Select value={formData.evaformat || ''} onChange={handleChange('evaformat')} displayEmpty>
            <MenuItem value="01">{t('companyEdit.evaMetric', 'kg/m²/hr')}</MenuItem>
            <MenuItem value="02">{t('companyEdit.evaImperial', 'lb/ft²/hr')}</MenuItem>
          </Select>
        </FormControl>,
      )}

      {fieldRow(
        t('settings.densityUnits', 'Density Units'),
        <FormControl size="small" fullWidth disabled={!canEdit}>
          <Select value={formData.densityformat || ''} onChange={handleChange('densityformat')} displayEmpty>
            <MenuItem value="01">{t('companyEdit.densityKg', 'kg/m³')}</MenuItem>
            <MenuItem value="02">{t('companyEdit.densityLbYd', 'lb/yd³')}</MenuItem>
            <MenuItem value="03">{t('companyEdit.densityLbFt', 'lb/ft³')}</MenuItem>
          </Select>
        </FormControl>,
      )}

      {fieldRow(
        t('settings.windspeedUnits', 'Windspeed Units'),
        <FormControl size="small" fullWidth disabled={!canEdit}>
          <Select value={formData.windspeedformat || ''} onChange={handleChange('windspeedformat')} displayEmpty>
            <MenuItem value="01">{t('companyEdit.windKmh', 'km/h')}</MenuItem>
            <MenuItem value="02">{t('companyEdit.windMph', 'mph')}</MenuItem>
          </Select>
        </FormControl>,
      )}

      {canEdit && saveBar}
    </>
  );

  const renderContent = () => {
    if (activeSection === 'users') {
      // The Users tab reuses the full Users page. Actions are per-row, so
      // there is deliberately no Save button here.
      return <UsersPage />;
    }
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }
    if (!company) {
      return <Alert severity="error">{error || t('settings.fetchError', 'Failed to load settings')}</Alert>;
    }
    switch (activeSection) {
      case 'general':
        return renderGeneral();
      case 'units':
        return renderUnits();
      default:
        return renderClientDetails();
    }
  };

  // Same shell as PourDashboardPage: a fixed-height area with a flush grey
  // section rail on desktop, a horizontal chip strip below md, and an
  // internally-scrolling content pane.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Full height under the 64px app bar (the negative margins cancel main's padding).
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        mx: -3,
        mt: -1.5,
        mb: { xs: 0, md: -3 },
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1,
          minHeight: 0,
          overflow: { xs: 'visible', md: 'hidden' },
        }}
      >
        {/* Section navigation — desktop rail, chip strip below md */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            gap: 1,
            px: 1.5,
            py: 1,
            flexShrink: 0,
            overflowX: 'auto',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {sections.map((s) => (
            <Chip
              key={s.key}
              label={s.label}
              onClick={() => navigate(`/settings/${s.key}`)}
              size="small"
              sx={{
                flexShrink: 0,
                borderRadius: '999px',
                fontWeight: 500,
                height: 30,
                border: '1px solid',
                borderColor: activeSection === s.key ? 'primary.main' : 'grey.300',
                bgcolor: activeSection === s.key ? 'primary.main' : 'grey.100',
                color: activeSection === s.key ? 'primary.contrastText' : 'text.secondary',
                '&:hover': { bgcolor: activeSection === s.key ? 'primary.dark' : 'grey.200' },
              }}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: 200,
            flexShrink: 0,
            bgcolor: '#f0f0f0',
            overflow: 'auto',
          }}
        >
          <List disablePadding sx={{ px: 1, pt: 1 }}>
            {sections.map((s) => (
              <ListItemButton
                key={s.key}
                selected={activeSection === s.key}
                onClick={() => navigate(`/settings/${s.key}`)}
                sx={{
                  py: 1,
                  px: 1.5,
                  mb: 0.25,
                  borderRadius: '6px',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.06)',
                    color: 'text.primary',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0,0,0,0.08)',
                    color: 'text.primary',
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    borderRadius: '0 6px 6px 0',
                    pl: 1.25,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>{s.icon}</ListItemIcon>
                <ListItemText
                  primary={s.label}
                  primaryTypographyProps={{
                    fontSize: '0.82rem',
                    fontWeight: activeSection === s.key ? 600 : 400,
                    noWrap: true,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Content area — scrolls internally on desktop, flows on mobile */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: { xs: 'visible', md: 'auto' }, p: { xs: 1.5, md: 2 } }}>
          <Paper elevation={2} sx={{ p: 3, minHeight: { md: '100%' } }}>
            {error && activeSection !== 'users' && company && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {renderContent()}
          </Paper>
        </Box>
      </Box>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success" onClose={() => setSuccess(false)} variant="filled">
          {t('settings.saved', 'Settings saved')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
